/* src/features/5DD/NutritionView.js */
import React, { useState, useEffect } from 'react';
import { historyService } from './historyService';
import BioSummary from './BioSummary'; 
import MealLogger from './MealLogger';
import { addDays, format, parseISO } from 'date-fns';

const styles = {
  container: { color: '#fff', padding: '10px', maxWidth: '800px', margin: '0 auto' },
  header: { marginBottom: '20px', borderBottom: '1px solid #555', paddingBottom: '10px' },
  select: {
    width: '100%', padding: '12px', fontSize: '16px', 
    borderRadius: '8px', border: 'none', marginTop: '5px', marginBottom: '20px'
  },
  dayCard: {
    backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '12px',
    padding: '15px', marginBottom: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
  },
  cardHeaderRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px'
  },
  dayTitle: { margin: 0, fontSize: '1.2em', color: '#ffd700' },
  habitBar: { display: 'flex', gap: '15px', alignItems: 'center', fontSize: '1.2em' },
  habitItem: { display: 'flex', alignItems: 'center', gap: '4px' },
  iconActive: { filter: 'grayscale(0)', opacity: 1 },
  iconInactive: { filter: 'grayscale(100%)', opacity: 0.3 },
  macroGrid: { 
    display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '5px', marginBottom: '15px', 
    backgroundColor: 'rgba(0,0,0,0.2)', padding: '8px', borderRadius: '8px'
  },
  macroItem: { textAlign: 'center', fontSize: '0.8em' },
  macroValue: { display: 'block', fontSize: '1.1em', fontWeight: 'bold' },
  mealList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  mealItem: { 
    display: 'flex', alignItems: 'center', gap: '12px', 
    backgroundColor: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '8px' 
  },
  thumb: { width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover', cursor: 'pointer', border: '1px solid #555' },
  lightboxOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.95)', zIndex: 2000,
    display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px'
  },
  lightboxContent: {
    maxWidth: '100%', maxHeight: '90%', display: 'flex', flexDirection: 'column', alignItems: 'center',
    color: '#fff', textAlign: 'center'
  },
  lightboxImg: { maxWidth: '100%', maxHeight: '60vh', borderRadius: '8px', marginBottom: '20px' },
  closeButton: {
    position: 'absolute', top: '20px', right: '20px',
    background: 'transparent', border: 'none', color: '#fff', fontSize: '3em', cursor:'pointer'
  }
};

function NutritionView({ session, profile, activeCourseId, refreshTrigger, onDataUpdate }) { 
  const [history, setHistory] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('active'); 
  const [dailyData, setDailyData] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [previewMeal, setPreviewMeal] = useState(null); 

  useEffect(() => {
    historyService.getHistoryIndex(session.user.id).then(setHistory);
  }, [session.user.id]);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        let start, end;
        
        if (selectedCourseId === 'active') {
          if (!profile?.course_start_date) { setDailyData([]); return; }
          start = new Date(profile.course_start_date);
          end = addDays(start, 6); 
        } else {
          const item = history.find(h => h.id === selectedCourseId);
          if (item) {
            start = new Date(item.start_date);
            end = new Date(item.end_date);
          }
        }

        if (start && end) {
            const startISO = start.toISOString();
            const endISO = end.toISOString();

            const [mealsData, habitsData] = await Promise.all([
                historyService.getCourseMeals(session.user.id, startISO, endISO),
                historyService.getDailyHabits(session.user.id, startISO, endISO)
            ]);

            const merged = mergeData(mealsData || [], habitsData || []);
            setDailyData(merged);
        }
      } catch (err) {
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [selectedCourseId, profile, history, session.user.id, refreshTrigger]);

  const mergeData = (meals, habits) => {
      const map = {};
      
      habits.forEach(h => { map[h.date] = { date: h.date, habit: h, meals: [] }; });

      meals.forEach(m => {
          // --- FORCE UTC FIX START ---
																					  
																			
											   
																		
          let isoString = m.date_logged;
          if (!isoString.endsWith('Z') && !isoString.includes('+')) {
              isoString += 'Z';
          }

          const dateKey = format(parseISO(isoString), 'yyyy-MM-dd');
          // --- FORCE UTC FIX END ---

          if (!map[dateKey]) map[dateKey] = { date: dateKey, habit: null, meals: [] };
          map[dateKey].meals.push(m);
      });

      return Object.values(map).sort((a,b) => b.date.localeCompare(a.date));
  };

  const calculateMacros = (dayMeals) => {
    let p=0, c=0, f=0, fib=0, cal=0;
    dayMeals.forEach(m => {
        if (m.ai_analysis?.nutrition) {
            p += m.ai_analysis.nutrition.protein_g || 0;
            c += m.ai_analysis.nutrition.carbs_g || 0;
            f += m.ai_analysis.nutrition.fat_g || 0;
            fib += m.ai_analysis.nutrition.fiber_g || 0;
            cal += m.ai_analysis.nutrition.calories || 0;
        }
    });
    return { p, c, f, fib, cal };
  };

  return (
    <div style={styles.container}>
      
      {selectedCourseId === 'active' && (
          <div style={{marginBottom: '30px'}}>
             <MealLogger session={session} onLogComplete={onDataUpdate} />
          </div>
      )}

      <hr style={{borderColor: '#555', margin: '20px 0'}} />

      <div style={styles.header}>
        <label>View History:</label>
        <select 
          style={styles.select}
          value={selectedCourseId}
          onChange={(e) => setSelectedCourseId(e.target.value)}
        >
          <option value="active">Current Active Course</option>
          {history.map(h => (
            <option key={h.id} value={h.id}>
              {format(new Date(h.start_date), 'MMM d')} - {h.user_note || 'Untitled'}
            </option>
          ))}
        </select>
        
        <BioSummary 
            session={session} 
            profile={profile} 
            activeCourseId={activeCourseId} 
        />
      </div>

      {loading && <p>Loading history...</p>}

      {!loading && dailyData.map((day) => {
          const macros = calculateMacros(day.meals);
          const h = day.habit || {}; 
        
          return (
            <div key={day.date} style={styles.dayCard}>
                <div style={styles.cardHeaderRow}>
                    <h3 style={styles.dayTitle}>{format(parseISO(day.date), 'EEE, MMM do')}</h3>
                    
                    <div style={styles.habitBar}>
                        <div style={styles.habitItem} title="HH5 Sessions">
                            <span style={{fontSize:'0.9em'}}>🧘</span>
                            <span style={{color: (h.hh5_count >= 3) ? '#ffd700' : '#aaa', fontWeight:'bold'}}>
                                {h.hh5_count || 0}/3
                            </span>
                        </div>
                        <span style={h.workout_completed ? styles.iconActive : styles.iconInactive}>🏋️</span>
                        <span style={h.sleep_logged ? styles.iconActive : styles.iconInactive}>💤</span>
                    </div>
                </div>
                
                <div style={styles.macroGrid}>
                    <div style={styles.macroItem}><span style={{...styles.macroValue, color:'#ffd700'}}>{macros.cal}</span>Cal</div>
                    <div style={styles.macroItem}><span style={styles.macroValue}>{macros.p}g</span>P</div>
                    <div style={styles.macroItem}><span style={styles.macroValue}>{macros.c}g</span>C</div>
                    <div style={styles.macroItem}><span style={styles.macroValue}>{macros.f}g</span>F</div>
                    <div style={styles.macroItem}><span style={styles.macroValue}>{macros.fib}g</span>Fib</div>
                </div>

                <div style={styles.mealList}>
                    {day.meals.map(meal => (
                        <div key={meal.id} style={styles.mealItem}>
                            {meal.image_url && (
                                <img 
                                  src={meal.image_url} 
                                  alt="Meal" 
                                  style={styles.thumb} 
                                  onClick={() => setPreviewMeal(meal)} 
                                  crossOrigin="anonymous" 
                                />
                            )}
                            <div style={{flex:1}}>
                                <div style={{fontWeight:'bold', fontSize:'0.95em'}}>{meal.comments || 'Meal'}</div>
                                <div style={{fontSize:'0.8em', color:'#ccc', lineHeight:'1.2'}}>
                                    {meal.ai_analysis?.summary || 'No analysis'}
                                </div>
                            </div>
                        </div>
                    ))}
                    {day.meals.length === 0 && <div style={{fontStyle:'italic', color:'#777', padding:'10px'}}>No meals logged</div>}
                </div>
            </div>
          );
      })}
      
      {previewMeal && (
        <div style={styles.lightboxOverlay} onClick={() => setPreviewMeal(null)}>
            <button style={styles.closeButton}>&times;</button>
            <div style={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
                <img 
                  src={previewMeal.image_url} 
                  alt="Full" 
                  style={styles.lightboxImg} 
                  crossOrigin="anonymous" 
                />
                <div style={{backgroundColor:'rgba(50,50,50,0.9)', padding:'15px', borderRadius:'8px'}}>
                    <h3>{previewMeal.comments}</h3>
                </div>
            </div>
        </div>
      )}

    </div>
  );
}

export default NutritionView;